import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, MapPin, Star, Home, Phone, MessageCircle,
  Search, X, Award, Clock, Languages, Key, TrendingUp, Ruler, FileText, Compass, IndianRupee
} from 'lucide-react';
import '../styles/Agents.css';


/* ─────────────────────────────────────────
   Floating Background Icons
   Used in: Recommendations + Services sections
───────────────────────────────────────── */
const FLOAT_ICONS = [
  // [Icon, size, top%, left%, animDuration, animDelay, animation]
  { Icon: Home,       size: 78, top: '12%',  left: '2%',   dur: '7s',   delay: '0s',    anim: 'geoFloat' },
  { Icon: MapPin,     size: 66, top: '65%',  left: '18%', dur: '9s',   delay: '0s',  anim: 'geoPulse' },
  { Icon: Key,        size: 70, top: '35%',  left: '94%',  dur: '8s',   delay: '0s',  anim: 'geoFloat' },
  { Icon: Building2,  size: 72, top: '72%',  left: '75%',  dur: '10s',  delay: '0s',    anim: 'geoPulse' },
  { Icon: TrendingUp, size: 64, top: '8%',   left: '55%',  dur: '6.5s', delay: '0s',  anim: 'geoFloat' },
  { Icon: Ruler,      size: 68, top: '50%',  left: '40%',  dur: '7.5s', delay: '0s',  anim: 'geoPulse' },
  { Icon: FileText,   size: 62, top: '80%',  left: '30%',   dur: '8.5s', delay: '0s',    anim: 'geoFloat' },
  { Icon: Compass,    size: 62, top: '25%',  left: '65%', dur: '11s',  delay: '0s',  anim: 'geoPulse' },
  { Icon: IndianRupee, size: 60, top: '90%',  left: '85%',  dur: '7s',   delay: '0s',  anim: 'geoFloat' },
  { Icon: Home,       size: 68, top: '55%',  left: '50%',  dur: '9.5s', delay: '0s',  anim: 'geoPulse' },
];

const floatAnimStyles = `
  @keyframes geoFloat {
    0%   { transform: translateY(0px);    opacity: 0.13; }
    50%  { transform: translateY(-18px);  opacity: 0.22; }
    100% { transform: translateY(0px);    opacity: 0.13; }
  }
  @keyframes geoPulse {
    0%   { transform: scale(1);    opacity: 0.10; }
    50%  { transform: scale(1.12); opacity: 0.20; }
    100% { transform: scale(1);    opacity: 0.10; }
  }
`;

const FloatingIcons = () => (
  <>
    <style>{floatAnimStyles}</style>
    {FLOAT_ICONS.map(({ Icon, size, top, left, dur, delay, anim }, i) => (
      <div
        key={i}
        style={{
          position: 'absolute',
          top,
          left,
          zIndex: 0,
          pointerEvents: 'none',
          animation: `${anim} ${dur} ease-in-out ${delay} infinite`,
          color: 'rgba(101, 78, 52, 0.55)',   /* warm brown to match cream bg */
        }}
      >
        <Icon size={size} strokeWidth={1.2} />
      </div>
    ))}
  </>
);


/* ── Hardcoded agent data ── */
const AGENTS = [
    {
    id: 1,
    name: 'Priya Sharma',
    photo: 'https://randomuser.me/api/portraits/women/44.jpg',
    city: 'Mumbai',
    phone: '+91 98765 12001',
    specialty: "Buyer's Agent",
    rating: 4.9,
    reviews: 128,
    listings: 34,
    experience: 9,
    languages: ['Hindi', 'English', 'Marathi'],
    bio: 'Specialises in luxury apartments in South Mumbai and Bandra. Known for transparent dealings and fast closures.',
  },
  {
    id: 2,
    name: 'Arjun Mehta',
    photo: 'https://randomuser.me/api/portraits/men/32.jpg',
    city: 'Pune',
    phone: '+91 98765 12002',
    specialty: "Seller's Agent",
    rating: 4.7,
    reviews: 95,
    listings: 21,
    experience: 6,
    languages: ['Hindi', 'English', 'Marathi'],
    bio: 'Expert in Kothrud and Wakad residential market. Consistently achieves above-asking prices for clients.',
  },
  {
    id: 3,
    name: 'Neha Rao',
    photo: 'https://randomuser.me/api/portraits/women/68.jpg',
    city: 'Bengaluru',
    phone: '+91 98765 12003',
    specialty: 'Rental Agent',
    rating: 4.8,
    reviews: 212,
    listings: 58,
    experience: 11,
    languages: ['Kannada', 'English', 'Hindi'],
    bio: 'Go-to agent for tech professionals relocating to Whitefield and Koramangala. Zero brokerage surprises.',
  },
  {
    id: 4,
    name: 'Ravi Krishnan',
    photo: 'https://randomuser.me/api/portraits/men/71.jpg',
    city: 'Hyderabad',
    phone: '+91 98765 12004',
    specialty: "Buyer's Agent",
    rating: 4.6,
    reviews: 74,
    listings: 19,
    experience: 5,
    languages: ['Telugu', 'Hindi', 'English'],
    bio: 'Deep knowledge of Gachibowli and HITEC City corridors. Helps IT professionals find homes near their workplace.',
  },
  {
    id: 5,
    name: 'Sunita Desai',
    photo: 'https://randomuser.me/api/portraits/women/12.jpg',
    city: 'Mumbai',
    phone: '+91 98765 12005',
    specialty: 'Rental Agent',
    rating: 4.5,
    reviews: 166,
    listings: 47,
    experience: 8,
    languages: ['Hindi', 'Gujarati', 'English'],
    bio: 'Handles furnished and semi-furnished rentals across Andheri and Powai. Quick turnaround guaranteed.',
  },
  {
    id: 6,
    name: 'Vikram Nair',
    photo: 'https://randomuser.me/api/portraits/men/55.jpg',
    city: 'Bengaluru',
    phone: '+91 98765 12006',
    specialty: "Seller's Agent",
    rating: 4.9,
    reviews: 88,
    listings: 26,
    experience: 13,
    languages: ['Malayalam', 'English', 'Kannada'],
    bio: 'Premium villa and independent house sales in HSR Layout and Sarjapur Road. 13 years of trust.',
  },
  {
    id: 7,
    name: 'Pooja Verma',
    photo: 'https://randomuser.me/api/portraits/women/29.jpg',
    city: 'Pune',
    phone: '+91 98765 12007',
    specialty: "Buyer's Agent",
    rating: 4.8,
    reviews: 143,
    listings: 38,
    experience: 7,
    languages: ['Hindi', 'English'],
    bio: 'Helps first-time homebuyers navigate Hinjewadi and Baner with ease. Patient, thorough, reliable.',
  },
  {
    id: 8,
    name: 'Deepak Shetty',
    photo: 'https://randomuser.me/api/portraits/men/18.jpg',
    city: 'Mumbai',
    phone: '+91 98765 12008',
    specialty: "Buyer's Agent",
    rating: 4.7,
    reviews: 61,
    listings: 15,
    experience: 4,
    languages: ['Konkani', 'Hindi', 'English'],
    bio: 'Young, sharp agent focused on new launches in Thane and Navi Mumbai. Strong negotiator.',
  },
  {
    id: 9,
    name: 'Lakshmi Iyer',
    photo: 'https://randomuser.me/api/portraits/women/55.jpg',
    city: 'Hyderabad',
    phone: '+91 98765 12009',
    specialty: 'Rental Agent',
    rating: 4.6,
    reviews: 109,
    listings: 33,
    experience: 6,
    languages: ['Tamil', 'Telugu', 'English'],
    bio: 'Focused on expat and NRI rental needs in Jubilee Hills and Banjara Hills. Handles documentation end to end.',
  },
  {
    id: 10,
    name: 'Suresh Pillai',
    photo: 'https://randomuser.me/api/portraits/men/46.jpg',
    city: 'Bengaluru',
    phone: '+91 98765 12010',
    specialty: "Buyer's Agent",
    rating: 4.5,
    reviews: 57,
    listings: 22,
    experience: 5,
    languages: ['Malayalam', 'Kannada', 'English'],
    bio: 'Focuses on budget homes in Electronic City and BTM Layout. Great for young families.',
  },
  {
    id: 11,
    name: 'Anjali Kapoor',
    photo: 'https://randomuser.me/api/portraits/women/82.jpg',
    city: 'Pune',
    phone: '+91 98765 12011',
    specialty: "Seller's Agent",
    rating: 4.9,
    reviews: 178,
    listings: 44,
    experience: 10,
    languages: ['Hindi', 'English', 'Punjabi'],
    bio: 'Top-rated sellers agent in Aundh and Pashan. Gets your listing maximum eyeballs in minimum time.',
  },
  {
    id: 12,
    name: 'Mohammed Rafi',
    photo: 'https://randomuser.me/api/portraits/men/62.jpg',
    city: 'Hyderabad',
    phone: '+91 98765 12012',
    specialty: "Seller's Agent",
    rating: 4.7,
    reviews: 83,
    listings: 28,
    experience: 8,
    languages: ['Urdu', 'Telugu', 'Hindi', 'English'],
    bio: 'Specialist in Old City and Secunderabad heritage properties. Unique market knowledge, unmatched network.',
  },
  // Delhi Agents
  {
    id: 13,
    name: 'Rahul Malhotra',
    photo: 'https://randomuser.me/api/portraits/men/40.jpg',
    city: 'Delhi',
    phone: '+91 98765 12013',
    specialty: "Buyer's Agent",
    rating: 4.8,
    reviews: 132,
    listings: 31,
    experience: 9,
    languages: ['Hindi', 'English'],
    bio: 'Works extensively in South Delhi and Dwarka premium residential market.',
  },
  {
    id: 14,
    name: 'Simran Kaur',
    photo: 'https://randomuser.me/api/portraits/women/65.jpg',
    city: 'Delhi',
    phone: '+91 98765 12014',
    specialty: 'Rental Agent',
    rating: 4.6,
    reviews: 90,
    listings: 24,
    experience: 5,
    languages: ['Hindi', 'English', 'Punjabi'],
    bio: 'Helps professionals find rental apartments in Gurgaon and Noida.',
  },

  // Chennai Agents
  {
    id: 15,
    name: 'Karthik Raman',
    photo: 'https://randomuser.me/api/portraits/men/77.jpg',
    city: 'Chennai',
    phone: '+91 98765 12015',
    specialty: "Seller's Agent",
    rating: 4.7,
    reviews: 110,
    listings: 27,
    experience: 8,
    languages: ['Tamil', 'English'],
    bio: 'Known for fast property sales in OMR and Anna Nagar.',
  },
  {
    id: 16,
    name: 'Meera Subramanian',
    photo: 'https://randomuser.me/api/portraits/women/23.jpg',
    city: 'Chennai',
    phone: '+91 98765 12016',
    specialty: "Buyer's Agent",
    rating: 4.9,
    reviews: 154,
    listings: 41,
    experience: 10,
    languages: ['Tamil', 'English', 'Hindi'],
    bio: 'Specialises in family apartments and gated communities across Chennai.',
  },

  // Kolkata Agents
  {
    id: 17,
    name: 'Amitava Sen',
    photo: 'https://randomuser.me/api/portraits/men/14.jpg',
    city: 'Kolkata',
    phone: '+91 98765 12017',
    specialty: 'Rental Agent',
    rating: 4.5,
    reviews: 72,
    listings: 18,
    experience: 6,
    languages: ['Bengali', 'Hindi', 'English'],
    bio: 'Focuses on affordable rentals in Salt Lake and New Town.',
  },
  {
    id: 18,
    name: 'Riya Banerjee',
    photo: 'https://randomuser.me/api/portraits/women/33.jpg',
    city: 'Kolkata',
    phone: '+91 98765 12018',
    specialty: "Seller's Agent",
    rating: 4.8,
    reviews: 121,
    listings: 29,
    experience: 7,
    languages: ['Bengali', 'English'],
    bio: 'Experienced in premium flats and resale properties in central Kolkata.',
  },

  // Ahmedabad Agents
  {
    id: 19,
    name: 'Harsh Patel',
    photo: 'https://randomuser.me/api/portraits/men/85.jpg',
    city: 'Ahmedabad',
    phone: '+91 98765 12019',
    specialty: "Buyer's Agent",
    rating: 4.7,
    reviews: 98,
    listings: 26,
    experience: 8,
    languages: ['Gujarati', 'Hindi', 'English'],
    bio: 'Strong expertise in SG Highway and Satellite residential projects.',
  },
  {
    id: 20,
    name: 'Nisha Trivedi',
    photo: 'https://randomuser.me/api/portraits/women/91.jpg',
    city: 'Ahmedabad',
    phone: '+91 98765 12020',
    specialty: 'Rental Agent',
    rating: 4.6,
    reviews: 86,
    listings: 22,
    experience: 5,
    languages: ['Gujarati', 'English'],
    bio: 'Assists students and working professionals with rentals across Ahmedabad.',
  },
];

const CITIES = [
  'All Cities',
  'Mumbai',
  'Pune',
  'Hyderabad',
  'Bengaluru',
  'Delhi',
  'Chennai',
  'Kolkata',
  'Ahmedabad',
];
const SPECIALTIES = ['All Specialties', "Buyer's Agent", "Seller's Agent", 'Rental Agent'];
const RATINGS     = [
  { label: 'Any Rating', value: 0 },
  { label: '4.5 & above', value: 4.5 },
  { label: '4.8 & above', value: 4.8 },
];

const SPECIALTY_COLORS = {
  "Buyer's Agent":  { bg: '#e8f4ec', text: '#1b5e3b' },
  "Seller's Agent": { bg: '#fdf0eb', text: '#b85c38' },
  'Rental Agent':   { bg: '#f0edf8', text: '#5b3fa0' },
};


/* ── Agent Card ── */
const AgentCard = ({ agent, selectedAgent, setSelectedAgent }) => {
  const sc = SPECIALTY_COLORS[agent.specialty];
  return (
    <div className="agent-card">
      <div className="agent-card-header">
        <img src={agent.photo} alt={agent.name} className="agent-photo" />
        <div className="agent-header-info">
          <h3 className="agent-name">{agent.name}</h3>
          <div className="agent-location">
            <MapPin size={13} />
            <span>{agent.city}</span>
          </div>
          <span
            className="agent-specialty-badge"
            style={{ background: sc.bg, color: sc.text }}
          >
            {agent.specialty}
          </span>
        </div>
      </div>

      <p className="agent-bio">{agent.bio}</p>

      <div className="agent-stats-row">
        <div className="agent-stat-item">
          <Star size={14} fill="#f5a623" stroke="#f5a623" />
          <span className="agent-stat-value">{agent.rating}</span>
          <span className="agent-stat-label">({agent.reviews} reviews)</span>
        </div>
        <div className="agent-stat-item">
          <Home size={14} stroke="#7a6a58" />
          <span className="agent-stat-value">{agent.listings}</span>
          <span className="agent-stat-label">listings</span>
        </div>
        <div className="agent-stat-item">
          <Clock size={14} stroke="#7a6a58" />
          <span className="agent-stat-value">{agent.experience} yrs</span>
        </div>
      </div>

      <div className="agent-languages">
        <Languages size={13} className="lang-icon" />
        <span>{agent.languages.join(' · ')}</span>
      </div>

      <div className="agent-card-actions">

        <div className="contact-wrapper">
          <button
            className="agent-btn-secondary"
            onClick={() =>
              setSelectedAgent(
                selectedAgent === `msg-${agent.id}`
                  ? null
                  : `msg-${agent.id}`
              )
            }
          >
            <MessageCircle size={15} />
            Message
          </button>

          {selectedAgent === `msg-${agent.id}` && (
            <div className="contact-popup">
              {agent.phone}
            </div>
          )}
        </div>

        <div className="contact-wrapper">
          <button
            className="agent-btn-primary"
            onClick={() =>
              setSelectedAgent(
                selectedAgent === `contact-${agent.id}`
                  ? null
                  : `contact-${agent.id}`
              )
            }
          >
            <Phone size={15} />
            Contact
          </button>

          {selectedAgent === `contact-${agent.id}` && (
            <div className="contact-popup">
              {agent.phone}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

/* ── Main Page ── */
const Agents = () => {
  const navigate = useNavigate();

  /* Auth guard */
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: { pathname: '/agents' } } });
    }
  }, [navigate]);

  const [search,      setSearch]      = useState('');
  const [city,        setCity]        = useState('All Cities');
  const [specialty,   setSpecialty]   = useState('All Specialties');
  const [minRating,   setMinRating]   = useState(0);
  const [selectedAgent, setSelectedAgent] = useState(null);


  const filtered = AGENTS.filter((a) => {
    const matchSearch    = a.name.toLowerCase().includes(search.toLowerCase());
    const matchCity      = city === 'All Cities'          || a.city === city;
    const matchSpecialty = specialty === 'All Specialties' || a.specialty === specialty;
    const matchRating    = a.rating >= minRating;
    return matchSearch && matchCity && matchSpecialty && matchRating;
  });

  const activeFiltersCount = [
    city !== 'All Cities',
    specialty !== 'All Specialties',
    minRating > 0,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setCity('All Cities');
    setSpecialty('All Specialties');
    setMinRating(0);
    setSearch('');
  };

  return (
    <div className="agents-page">

       {/* Floating icons — behind all content */}
        <FloatingIcons />


      {/* ── Navbar ── */}
      <nav className="agents-nav">
        <div className="agents-nav-inner">
          <div className="agents-logo" onClick={() => navigate('/landing')}>
            <Building2 size={22} />
            <span>GeoState</span>
          </div>
          <button className="agents-back" onClick={() => navigate(-1)}>← Back</button>
        </div>
      </nav>

      {/* ── Page Header ── */}
      <div className="agents-hero">
        <div className="agents-hero-content">
          <br></br>
          <h1 className="agents-hero-title">Find the right agent for you</h1>
          <p className="agents-hero-sub">
            Browse verified agents across India's top cities — filter by city, specialty, and rating.
          </p>

          {/* Search bar */}
          <div className="agents-search-bar">
            <Search size={17} className="agents-search-icon" />
            <input
              type="text"
              placeholder="Search by agent name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="agents-search-input"
            />
            {search && (
              <button className="agents-search-clear" onClick={() => setSearch('')}>
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="agents-filter-bar">
        <div className="agents-filter-inner">

          {/* City */}
          <select
            className="agents-filter-select"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          >
            {CITIES.map((c) => <option key={c}>{c}</option>)}
          </select>

          {/* Specialty */}
          <select
            className="agents-filter-select"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
          >
            {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
          </select>

          {/* Rating */}
          <select
            className="agents-filter-select"
            value={minRating}
            onChange={(e) => setMinRating(Number(e.target.value))}
          >
            {RATINGS.map((r) => (
              <option key={r.label} value={r.value}>{r.label}</option>
            ))}
          </select>

          {/* Clear */}
          {activeFiltersCount > 0 && (
            <button className="agents-clear-btn" onClick={clearFilters}>
              <X size={14} />
              Clear filters
              <span className="filter-count-badge">{activeFiltersCount}</span>
            </button>
          )}

          <div className="agents-results-count">
            {filtered.length} agent{filtered.length !== 1 ? 's' : ''} found
          </div>
        </div>
      </div>

      {/* ── Agent Grid ── */}

      {/* Floating icons — behind all content */}
        <FloatingIcons />

      <div className="agents-grid-section">
        {filtered.length > 0 ? (
          <div className="agents-grid">
            {filtered.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
              />
            ))}
          </div>
        ) : (
          <div className="agents-empty">
            <Award size={48} className="agents-empty-icon" />
            <h3>No agents match your filters</h3>
            <p>Try adjusting your search or clearing some filters.</p>
            <button className="agents-clear-btn-lg" onClick={clearFilters}>Clear all filters</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Agents;